/* Dropdown. Design notes live in Dropdown.css.

   Disclosure — panel holds whatever you put in it:

     <div class="dropdown">
       <button type="button" class="dropdown-trigger">Husleje</button>
       <div class="dropdown-panel" hidden>…</div>
     </div>

   Combobox — trigger is a text input, panel is a list of options that filter
   as you type:

     <div class="dropdown">
       <input type="text" class="dropdown-trigger" role="combobox"
              aria-autocomplete="list" placeholder="Hvor vil du bo?">
       <div class="dropdown-panel" role="listbox" hidden>
         <button type="button" class="dropdown-option" role="option">Dragør Kommune</button>
       </div>
     </div>

   Picking an option fires a bubbling `dropdown:select` with
   { value, option } — value is data-value if present, else the trimmed text.
   A combobox trigger also gets its value set and a `change` dispatched. */

let panelId = 0;

function wire(root: HTMLElement) {
  const trigger = root.querySelector<HTMLElement>('.dropdown-trigger');
  const panel = root.querySelector<HTMLElement>('.dropdown-panel');
  if (!trigger || !panel) return;

  const combobox = trigger instanceof HTMLInputElement ? trigger : null;

  if (!panel.id) panel.id = `dropdown-panel-${++panelId}`;
  trigger.setAttribute('aria-controls', panel.id);
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-haspopup', 'listbox');
  panel.hidden = true;

  const allOptions = () => Array.from(panel.querySelectorAll<HTMLElement>('.dropdown-option'));
  const shownOptions = () => allOptions().filter((option) => !option.hidden);

  // Added only while open, so there's never a stray document listener behind a
  // closed dropdown and nothing to unregister on teardown.
  const onDocumentMouseDown = (event: MouseEvent) => {
    if (!root.contains(event.target as Node)) close();
  };

  const open = () => {
    if (!panel.hidden) return;
    panel.hidden = false;
    root.dataset.open = '';
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('mousedown', onDocumentMouseDown);
  };

  const close = (focusTrigger = false) => {
    if (panel.hidden) return;
    panel.hidden = true;
    delete root.dataset.open;
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', onDocumentMouseDown);
    if (focusTrigger) trigger.focus();
  };

  const moveFocus = (step: 1 | -1) => {
    const options = shownOptions();
    if (!options.length) return;
    const current = options.indexOf(document.activeElement as HTMLElement);
    const next =
      current === -1
        ? step === 1
          ? 0
          : options.length - 1
        : (current + step + options.length) % options.length;
    options[next].focus();
  };

  // Plain lowercase substring: Danish readers type æ/ø/å, and those are single
  // code points, so there is nothing for diacritic folding to do here.
  const applyFilter = () => {
    const query = combobox!.value.trim().toLowerCase();
    allOptions().forEach((option) => {
      option.hidden = query !== '' && !(option.textContent ?? '').toLowerCase().includes(query);
    });
  };

  if (combobox) {
    // Deliberately not `focus`: close() hands focus back to the trigger, so a
    // focus-opens rule reopens the panel the instant you pick something. Also
    // stops panels popping open at every tab stop. ArrowDown opens from the
    // keyboard.
    combobox.addEventListener('click', open);
    combobox.addEventListener('input', () => {
      applyFilter();
      open();
    });
  } else {
    trigger.addEventListener('click', () => (panel.hidden ? open() : close()));
  }

  root.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    if (panel.hidden) {
      event.preventDefault();
      open();
      // hidden flips synchronously, so the options are focusable right away.
      moveFocus(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    // Nothing to rove between — a multi-select's panel holds checkboxes, not
    // .dropdown-options. Leave the key alone so it still scrolls the panel.
    if (!shownOptions().length) return;

    event.preventDefault();
    moveFocus(event.key === 'ArrowDown' ? 1 : -1);
  });

  panel.addEventListener('click', (event: MouseEvent) => {
    const option = (event.target as HTMLElement).closest<HTMLElement>('.dropdown-option');
    if (!option || !panel.contains(option)) return;

    allOptions().forEach((other) =>
      other.setAttribute('aria-selected', String(other === option)),
    );

    const value = option.dataset.value ?? option.textContent?.trim() ?? '';
    if (combobox) {
      combobox.value = value;
      // `change`, not `input` — `input` would re-run applyFilter and narrow the
      // list to the thing that was just picked.
      combobox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    root.dispatchEvent(
      new CustomEvent('dropdown:select', { bubbles: true, detail: { value, option } }),
    );
    close(true);
  });
}

export default function initDropdown(scope: ParentNode = document) {
  scope.querySelectorAll<HTMLElement>('.dropdown').forEach(wire);
}
